import React from 'react';
import type { ChartLegendSeries } from './ChartLegend';

export interface ChartLegendStripProps {
  series: ChartLegendSeries[];
  hiddenKeys: Set<string>;
  onToggle: (key: string) => void;
}

/**
 * Chart-DS legend: a horizontal bottom-left strip — 7px color dots, 10px
 * labels, click to toggle a series. The floating draggable panel
 * (ChartLegend) remains available behind ChartSpec.legendVariant 'floating'.
 */
const ChartLegendStrip: React.FC<ChartLegendStripProps> = ({ series, hiddenKeys, onToggle }) => {
  if (series.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-4 select-none">
      {series.map((s) => {
        const isHidden = hiddenKeys.has(s.key);
        return (
          <button
            key={s.key}
            type="button"
            onClick={() => onToggle(s.key)}
            title={isHidden ? `Show ${s.name}` : `Hide ${s.name}`}
            className={`flex items-center gap-1.5 transition-opacity ${isHidden ? 'opacity-40' : ''}`}
          >
            <span
              className="rounded-full shrink-0"
              style={{ width: 7, height: 7, backgroundColor: isHidden ? 'transparent' : s.color, border: `1.5px solid ${s.color}` }}
            />
            <span className="text-[10px] font-medium text-neutral-500">{s.name}</span>
            {s.value != null && (
              <span className="text-[10px] tabular-nums text-neutral-400">
                {typeof s.value === 'number'
                  ? (s.formatter ? s.formatter(s.value) : s.value.toLocaleString())
                  : s.value}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default ChartLegendStrip;
